/* eslint-disable @thebeansgroup/sb/components-file-structure */
import Markdown from 'markdown-to-jsx'
import PropTypes from 'prop-types'
import { Fragment, useEffect, useState } from 'react'

import { withI18n } from '@thebeansgroup/i18n'
import TickCircleIcon from '@thebeansgroup/ui_icons/tick_circle_icon'
import Callout from '@thebeansgroup/ui_typography/callout'
import Headline from '@thebeansgroup/ui_typography/headline'

import { ROUTES } from '@src/authorisation/children/router/constants'
import { asyncRequestWithFetch } from '@src/authorisation/helpers'
import Accordion from '@src/components/accordion'
import CloudflareTurnstile from '@src/components/cloudflare_turnstile'

import Layout from '@components/layout'
import ThemedButton from '@components/themed_button'

import { FAQ, RESEND_INTERVAL } from './constants'
import {
  getBackToLogIn,
  getDoubleCheck,
  getEmailResent,
  getError,
  getFAQ,
  getPageTitle,
  getResendIn,
  getResendResetLink,
  getTitle,
  getDescription,
  isButtonDisabled
} from './helpers'
import * as styles from './styles'

const EmailSentScreen = ({ token, t, email, logInClickHandler }) => {
  const [emailSent, setEmailSent] = useState(true)
  const [counter, setCounter] = useState(RESEND_INTERVAL)
  const [emailSuccess, setEmailSuccess] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState('')
  const [recaptchaError, setRecaptchaError] = useState(false)
  const [error, setError] = useState(false)

  /* istanbul ignore next */
  const resetError = () => {
    setRecaptchaError(false)
  }

  const endCounter = () => {
    setEmailSent(false)
  }

  useEffect(() => {
    let intervalId

    if (!emailSent || emailSuccess) return

    if (counter === 0) {
      endCounter()
    } else {
      intervalId = setInterval(() => {
        setCounter((prevCounter) => prevCounter - 1)
      }, 1000)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [emailSent, counter, emailSuccess])

  const handleSuccess = () => {
    setEmailSuccess(true)

    setTimeout(() => {
      setEmailSent(true)
      setCounter(RESEND_INTERVAL)
      setEmailSuccess(false)
      setRecaptchaError(true)
    }, 3000)
  }

  const submitResetPasswordForm = async () => {
    setEmailSent(true)

    return await asyncRequestWithFetch(
      token,
      ROUTES.SERVER.RESET_PASSWORD,
      {
        user: {
          email
        },
        'cf-turnstile-response': recaptchaToken
      },
      () => handleSuccess(),
      () => setError(true)
    )
  }

  const renderFAQ = () => {
    return (
      <div css={styles.faq}>
        <Accordion title={getTitle(t)}>
          <Headline component='h5'>💡 {getDoubleCheck(t)}</Headline>
          <ul css={styles.faqList}>
            {FAQ.map(({ translation, fallback }) => (
              <li key={translation}>
                <Markdown>{getFAQ(t, translation, fallback)}</Markdown>
              </li>
            ))}
          </ul>
        </Accordion>
      </div>
    )
  }

  const renderBackToLoginButton = () => {
    return (
      <ThemedButton onClick={logInClickHandler}>
        {getBackToLogIn(t)}
      </ThemedButton>
    )
  }

  const renderButtonContent = () => {
    if (emailSuccess) {
      return (
        <Fragment>
          <span>{getEmailResent(t)}</span>{' '}
          <TickCircleIcon style={styles.icon} />
        </Fragment>
      )
    }

    if (emailSent) {
      return (
        <Fragment>
          {getResendIn(t)}
          <span css={styles.counter}>{counter}</span>
        </Fragment>
      )
    }

    return getResendResetLink(t)
  }

  const renderResendButton = () => {
    return (
      <ThemedButton
        type='secondary'
        disabled={isButtonDisabled(emailSent, emailSuccess, recaptchaToken)}
        onClick={submitResetPasswordForm}
      >
        {renderButtonContent()}
      </ThemedButton>
    )
  }

  const renderDescription = () => {
    return (
      <div css={styles.description}>
        <Callout>
          <Markdown>{getDescription(t)}</Markdown>
        </Callout>
      </div>
    )
  }

  const renderError = () => {
    if (!error) return null

    return <div css={styles.error}>{getError(t)}</div>
  }

  const renderTurnstile = () => {
    return (
      <CloudflareTurnstile
        onSuccess={setRecaptchaToken}
        onError={setRecaptchaError}
        error={recaptchaError}
        resetError={resetError}
        action='password_reset'
      />
    )
  }

  return (
    <Layout
      title={`${getPageTitle(t)} 🪄`}
      padding
    >
      {renderDescription()}
      {renderBackToLoginButton()}
      {renderResendButton()}
      {renderTurnstile()}
      {renderError()}
      {renderFAQ()}
    </Layout>
  )
}

EmailSentScreen.propTypes = {
  t: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  logInClickHandler: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired
}

export default withI18n(EmailSentScreen)
